import {AfterViewInit, Component, inject, OnDestroy, viewChild, signal, computed, DestroyRef, ChangeDetectionStrategy} from "@angular/core"
import {FormsModule} from "@angular/forms"
import {MatButtonModule} from "@angular/material/button"
import {MatInputModule} from "@angular/material/input"
import {MatProgressBarModule} from "@angular/material/progress-bar"
import {ActivatedRoute, Router, RouterOutlet} from "@angular/router"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {DialogSize} from "@common/models/dialogs"
import {ScannerControlInterface, ScanningCanvasToolbox, ScanRegion} from "@app/platform/models/scanning"
import {IsDefined} from "@cm/utils/filter"
import {CanvasBaseComponent} from "@common/components/canvas"
import {CM_LOGGER_SCANNING} from "@common/services/logging/logger.tokens"
import {blobToDataURL, forkJoinZeroOrMore} from "@legacy/helpers/utils"
import {Hotkeys} from "@common/services/hotkeys/hotkeys.service"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {SelectMaterialComponent} from "@platform/components/materials/select-material/select-material.component"
import {ScanningActionBarComponent} from "@platform/components/scanning/scanning-action-bar/scanning-action-bar.component"
import {OrganizationsWithScanningUrlGQL} from "@platform/components/scanning/scanning/scanning.generated"
import {ScanRegionMaterialGQL} from "@platform/models/scanning/scanning-canvas-toolbox/tool-items/scan-region.generated"
import {ScanningService} from "@platform/services/scanning/scanning.service"
import {Observable, of} from "rxjs"
import {ResolutionOption, ScanParameters, TablePosition} from "@platform/models/scanning/types"
import {AuthService} from "@common/services/auth/auth.service"
import {DialogService} from "@common/services/dialog/dialog.service"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-scanning",
    templateUrl: "./scanning.component.html",
    styleUrls: ["./scanning.component.scss"],
    imports: [MatProgressBarModule, ScanningActionBarComponent, MatButtonModule, MatInputModule, FormsModule, RouterOutlet, CanvasBaseComponent],
})
export class ScanningComponent implements AfterViewInit, OnDestroy {
    readonly $canvasBase = viewChild.required<CanvasBaseComponent>("scanningCanvas")
    readonly $scanningActionBar = viewChild.required<ScanningActionBarComponent>("scanningActionBar")
    readonly $selectMaterialDialog = viewChild.required<SelectMaterialComponent>("selectMaterialDialog")
    // Set this to true in order to allow testing the UI including the regions without an actual connection to the scanner.

    // Set this to true in order to allow testing the UI including the regions without an actual connection to the scanner.
    readonly $developmentMode = signal(false)
    readonly $developmentModeData = signal<{resolutionOptions: ResolutionOption[]; scanParams: ScanParameters}>({
        scanParams: {
            state: "scanning",
            captureReference: false,
            localScanName: "",
            mode: "cloud",
            regions: [],
            processTiles: true,
            tileSize: 10,
        },
        resolutionOptions: [
            {id: "debug1", name: "debugRes1", pixelsPerMm: 10},
            {id: "debug2", name: "debugRes2", pixelsPerMm: 20},
        ],
    })
    readonly $fallbackScanParams = signal<ScanParameters>({
        state: "aborted",
        captureReference: false,
        localScanName: "",
        mode: "local",
        regions: [],
        processTiles: true,
        tileSize: 10,
    })
    readonly $overviewImageUrl = signal<string | null>(null)
    readonly $overviewImageValid = signal(false)
    readonly $scanner = signal<ScannerControlInterface | null>(null)

    readonly $scannerUrl = signal<string | undefined>(undefined)
    readonly $connectionState = signal<ConnectionState>("notSetup")

    readonly DialogSize = DialogSize

    readonly $selectedScanRegion = signal<ScanRegion | null>(null)

    private toolbox?: ScanningCanvasToolbox

    readonly auth = inject(AuthService)
    readonly dialog = inject(DialogService)
    readonly organizations = inject(OrganizationsService)

    readonly materialGql = inject(ScanRegionMaterialGQL)
    private readonly router = inject(Router)
    private readonly route = inject(ActivatedRoute)
    private readonly scanningService = inject(ScanningService)
    private readonly hotkeys = inject(Hotkeys)
    private readonly destroyRef = inject(DestroyRef)
    readonly organizationsGql = inject(OrganizationsWithScanningUrlGQL)
    readonly logger = inject(CM_LOGGER_SCANNING)

    constructor() {
        if (this.$developmentMode()) {
            this.$connectionState.set("connected")
            this.$overviewImageUrl.set(
                "https://storage.googleapis.com/cm-platform-prod-media/th9NMyV56s9QVM1yapFOsOsbJsxSH2K6ttNY6jmNxd3oaNnpFzvIMO4HbkagAyoTsW2iiKXi" +
                    "kts9JpKYW8EtMGl9KEkUftID3hIdS8cN4tIzlEiVWwi4xHknrrMKmpLn-jpg",
            )
            this.$overviewImageValid.set(true)
        }
    }

    ngAfterViewInit() {
        this.toolbox = new ScanningCanvasToolbox(this.$canvasBase(), this.hotkeys, (materialLegacyId: number) =>
            fetchThrowingErrors(this.materialGql)({legacyId: materialLegacyId}).then((res) => res.material),
        )

        this.$canvasBase().toolbox = this.toolbox
        this.toolbox.selectionChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((region: ScanRegion | null) => {
            this.$selectedScanRegion.set(region!)
            if (region) {
                region.bringToFront()
                this.$scanningActionBar()?.activateScanRegionSettingsPanel()
            }
        })

        this.toolbox.scanRegionCreated.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((regionUi) => {
            regionUi.regionModified.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.regionModified(regionUi))
            this.openSelectMaterialDialog()
        })

        this.toolbox.scanRegionDeleted.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.updateScanParameters()
        })

        if (this.$developmentMode()) {
            this.toolbox.defaultResolution = this.$resolutionOptions()[0]
            this.$canvasBase().setPhysicalInfo({pixelsPerCm: 10, originXCm: 0, originYCm: 0})
        }
        // need to do this _outside_ of the current change detection context
        if (!this.$developmentMode()) {
            const scannerUrlParam = this.route.snapshot.queryParams["scanner-url"]

            if (typeof scannerUrlParam === "string" && scannerUrlParam.length > 0) {
                this.$scannerUrl.set(scannerUrlParam)
                this.connectToScanner()
            } else {
                this.organizations.ownIds
                    .then((organizationsIds) => {
                        if (!organizationsIds || organizationsIds.length === 0) {
                            throw new Error("No organizations found")
                        }
                        return fetchThrowingErrors(this.organizationsGql)({organizationIds: organizationsIds})
                    })
                    .then(({organizations}) => {
                        const scannerUrls = organizations.map((organization) => organization?.defaultScannerUrl).filter(IsDefined)
                        if (scannerUrls.length === 0) {
                            throw new Error("No scanner URL found")
                        } else {
                            this.$scannerUrl.set(scannerUrls[0])
                            this.connectToScanner()
                        }
                    })
            }
        }
    }

    private cleanupConnection(): void {
        this.$scanner()?.destroy()
        this.$scanner.set(null)
        this.scanningService.clearLogs()
        this.$overviewImageUrl.set(null)
        this.$overviewImageValid.set(false)
        this.$connectionState.set("notConnected")
    }

    connectToScanner(): void {
        this.cleanupConnection()
        this.$connectionState.set("connecting")
        const logReplayFn = (entry: string) => this.scanningService.log.next(entry)
        ScannerControlInterface.connect(`wss://${this.$scannerUrl()}:5555/`, this.logger, logReplayFn).subscribe({
            next: (scanner) => {
                this.$scanner.set(scanner)
                scanner.logEvent$.subscribe((entry) => this.scanningService.log.next(entry))
                this.$connectionState.set("connected")

                scanner.connectionClosed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
                    this.cleanupConnection()
                })

                this.toolbox!.defaultResolution = scanner.globalInfo.resolutionOptions[1]

                this.showOverview()
                this.loadScanParameters()
                this.loadScanInfo()
                this.updateEditMode()

                scanner.stateChangeEvent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({key}) => {
                    if (key === "scanParameters") {
                        this.loadScanParameters()
                    } else if (key === "scanInfo") {
                        this.loadScanInfo()
                    } else if (key === "overviewState") {
                        this.showOverview()
                    }
                    this.updateEditMode()
                })
            },
            error: () => {
                this.$connectionState.set("failed")
            },
        })
    }

    imageLoadingComplete(): void {
        this.$canvasBase().navigation.zoomToFitImage(0.95)
    }

    readonly $notReady = computed(() => {
        if (this.$developmentMode()) return false
        const scanner = this.$scanner()
        return !scanner || scanner.taskState === "busy"
    })

    readonly $taskInProgress = computed(() => this.$scanner()?.taskState === "busy")

    readonly $canStartScan = computed(() => {
        if (this.$developmentMode()) return true
        const scanner = this.$scanner()
        if (!scanner) return false
        const params = scanner.scanParameters
        if (this.$notReady() || params == null) {
            return false
        } else if (this.toolbox!.scanRegions.length == 0) {
            return false
        } else if (params.mode === "local") {
            return (params.localScanName ?? "").length > 0
        } else {
            for (const region of params.regions) {
                if (region.materialId == null) {
                    return false
                }
            }
            return true
        }
    })

    readonly $resolutionOptions = computed(() => {
        if (this.$developmentMode()) return this.$developmentModeData().resolutionOptions
        return this.$scanner()?.globalInfo.resolutionOptions ?? []
    })

    readonly $hasTransparencySupport = computed(() => this.$scanner()?.globalInfo.hasTransparencySupport ?? false)

    calibrateTable(useExisting: boolean): void {
        this.$scanner()?.calibrateTable(useExisting)
    }

    moveTable(positionName: TablePosition): void {
        this.$scanner()?.moveTable(positionName)
    }

    captureTestImage(): void {
        const scanner = this.$scanner()
        if (!scanner) return
        scanner.captureTestImage().subscribe(() => {
            const state = scanner.overviewState
            blobToDataURL(new Blob([state!.imageData! as Uint8Array<ArrayBuffer>], {type: "image/jpeg"})).subscribe((dataURL) => {
                //TODO: release data URL
                this.$overviewImageUrl.set(dataURL)
                this.$overviewImageValid.set(true)
            })
        })
    }

    startOverviewScan(regionSize?: "full" | "half" | "quarter"): void {
        this.$scanner()?.startOverviewScan(regionSize)
    }

    startBacklightCalibrationScan(): void {
        this.updateScanParameters()
        this.$scanner()?.startBacklightCalibrationScan()
    }

    readonly $overviewScanState = computed(() => this.$scanner()?.overviewState?.state)

    private showOverview(): void {
        const state = this.$scanner()?.overviewState
        if (!state) return
        if (state.state === "valid") {
            this.$canvasBase().setPhysicalInfo({
                pixelsPerCm: state.imagePixelsPerCm!,
                originXCm: state.imageRegion![0],
                originYCm: state.imageRegion![1],
            })
            blobToDataURL(new Blob([state.imageData! as Uint8Array<ArrayBuffer>], {type: "image/jpeg"})).subscribe((dataURL) => {
                //TODO: release data URL
                this.$overviewImageUrl.set(dataURL)
                this.$overviewImageValid.set(true)
            })
        } else {
            this.$overviewImageUrl.set(null)
            this.$overviewImageValid.set(false)
            this.$canvasBase().setPhysicalInfo({pixelsPerCm: 0, originXCm: 0, originYCm: 0})
        }
    }

    startScan(): void {
        this.updateScanParameters()
        this.$scanner()?.startScan()
    }

    cancelTask(): void {
        this.$scanner()?.cancel()
    }

    private updateEditMode(): void {
        const allowEdit = !this.$notReady()
        //this.canvasBase.navigation.mode = allowEdit ? "ScanRegionCreation" : "Panning";
        this.toolbox!.allowScanRegionCreation = allowEdit
        this.getUiScanRegions().forEach((region) => (region.allowEdit = allowEdit))
    }

    private getUiScanRegions(): ScanRegion[] {
        return this.toolbox!.scanRegions
    }

    private loadScanInfo(): void {
        const scanInfo = this.$scanner()?.scanInfo
        if (!scanInfo) return
        this.getUiScanRegions().forEach((regionUi, idx) => {
            const regionInfo = idx < scanInfo.regions.length ? scanInfo.regions[idx] : null
            regionUi.updateInfo(regionInfo)
        })
    }

    private loadScanParameters(): void {
        const scanner = this.$scanner()
        if (!scanner) return
        const pendingRegions: Observable<ScanRegion>[] = []
        const params = scanner.scanParameters
        const selectedScanRegion = this.$selectedScanRegion()
        const selectedIdx = selectedScanRegion ? this.getUiScanRegions().indexOf(selectedScanRegion) : -1
        // Cache loaded materials before destroying regions so recreated regions can reuse them
        const materialCache = new Map<number, ScanRegion["material"]>()
        for (const existingRegion of this.getUiScanRegions()) {
            if (existingRegion.materialId && existingRegion.material) {
                materialCache.set(existingRegion.materialId, existingRegion.material)
            }
        }
        this.toolbox!.removeAllItems()
        this.$selectedScanRegion.set(null)
        params?.regions?.forEach((region) => {
            const regionUi = this.toolbox!.addScanRegion(0, 0, 1, 1)
            regionUi.area = region.area
            regionUi.state = region.state
            const cachedMaterial = materialCache.get(region.materialId)
            if (cachedMaterial) {
                regionUi.setMaterialFromCache(region.materialId, cachedMaterial)
            } else {
                regionUi.materialId = region.materialId
            }
            regionUi.resolution = undefined
            for (const resOption of scanner.globalInfo.resolutionOptions) {
                if (resOption.id === region.resolutionId) {
                    regionUi.resolution = resOption
                    break
                }
            }
            regionUi.sampleRotation = region.sampleRotation
            regionUi.transparent = region.transparent
            regionUi.regionModified.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.regionModified(regionUi))
            pendingRegions.push(of(regionUi))
        })
        forkJoinZeroOrMore(pendingRegions).subscribe()
        this.loadScanInfo()
        const uiRegions = this.getUiScanRegions()
        this.$selectedScanRegion.set(selectedIdx < 0 || selectedIdx >= uiRegions.length ? null : uiRegions[selectedIdx])
        const newSelectedRegion = this.$selectedScanRegion()
        if (newSelectedRegion) {
            newSelectedRegion.selected = true
        }
    }

    private regionModified(regionUi: ScanRegion): void {
        if (this.$notReady() && !this.$developmentMode()) throw new Error("Tried to modify region while scanner was not ready!")
        regionUi.state = "editing"
        this.updateScanParameters()
    }

    updateScanParameters(): void {
        const scanner = this.$scanner()
        const params = this.$developmentMode() ? this.$developmentModeData().scanParams : scanner?.scanParameters
        if (!params) return
        params.regions = []
        this.getUiScanRegions().forEach((regionUi) => {
            if (!regionUi.materialId) {
                throw Error("Encountered material-id of null in updateScanParameters.")
            }
            if ((regionUi.physicalInfo?.pixelsPerCm ?? 0) === 0) {
                throw Error("Encountered scan region with invalid physical info in updateScanParameters.")
            }
            params.regions.push({
                state: regionUi.state!,
                materialId: regionUi.materialId,
                resolutionId: regionUi.resolution!.id,
                sampleRotation: regionUi.sampleRotation,
                transparent: regionUi.transparent!,
                area: regionUi.area,
            })
        })
        if (!this.$developmentMode() && scanner) scanner.scanParameters = params
    }

    showLogs(): void {
        this.router.navigate(["logs"], {relativeTo: this.route})
    }

    ngOnDestroy(): void {
        this.toolbox!.remove()
        this.$canvasBase().toolbox = null
        this.$scanner()?.destroy()
        this.$scanner.set(null)
    }

    openSelectMaterialDialog() {
        this.dialog.selectInDialog(SelectMaterialComponent, {
            filters: {meshSpecific: false},
            lockOrganization: !this.auth.$actingAsStaff(),
            onSelect: (material) => {
                const region = this.$selectedScanRegion()
                if (material && region) {
                    region.materialId = material.legacyId
                    region.state = "editing"
                    this.$scanningActionBar().refreshRangeTag()
                    this.updateScanParameters()
                }
            },
            onCancel: () => {
                const region = this.$selectedScanRegion()
                if (!region) return
                if (!region.material) {
                    region.remove()
                    this.$selectedScanRegion.set(null)
                }
            },
        })
    }
}

type ConnectionState = "notSetup" | "notConnected" | "connecting" | "connected" | "failed"
